import { z } from "zod";
import { Location, Shopify } from "@/types/general";

export const locationsMapperSchema = z.object({
    mappings: z.array(
        z
            .object({
                shopify_location: z.custom<Shopify.Location>().nullish(),
                suppli_location: z.custom<Location.Merged>().nullish(),
            })
            .superRefine((value, ctx) => {
                // if either is selected, the other must be selected as well
                if (value.shopify_location && !value.suppli_location) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: "Suppli location must be selected",
                    });
                }
                if (!value.shopify_location && value.suppli_location) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: "Shopify location must be selected",
                    });
                }
            })
    ),
});

export type LocationsMapperFormTyping = z.infer<typeof locationsMapperSchema>;

// Helper to generate unique key for Suppli location
export const getSuppliLocationKey = (loc: Location.Merged | null | undefined): string | null => {
    if (!loc) return null;
    return loc.section ? `${loc.store.id}_${loc.section.id}` : `${loc.store.id}`;
};

/**
 * Generate a label for a location option.
 * - If only store name: output it as-is
 * - If store + section name: truncate store name to 5 chars + "..." if long, then add section
 *
 * @param storeName - The primary name (e.g., store name)
 * @param sectionName - The secondary name (e.g., section name), optional
 */
export const generateLocationLabel = (storeName: string, sectionName?: string | null): string => {
    // If no section name, just return store name
    if (!sectionName) {
        return storeName;
    }

    // Both exist - truncate store name if needed
    const displayName = storeName.length > 5 ? `${storeName.slice(0, 5)}...` : storeName;
    return `${displayName} - ${sectionName}`;
};
